import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Upload, AlertCircle, AlertTriangle, X, Plus, Star, ChevronUp, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import { useQuery, useMutation } from '@apollo/client';
import { GET_CATEGORIES } from '../graphql/queries/categories';
import { GET_TOOL_BY_ID } from '../graphql/queries/getTool';
import { UPDATE_TOOL } from '../graphql/mutations/updateTool';
import ToolAvailabilityCalendar, { AvailabilityPeriod } from '../components/features/ToolAvailabilityCalendar';
import { useAuth } from '../context/AuthContext';
import { normalizeToolCondition, TOOL_CONDITION_OPTIONS, ToolCondition } from '../lib/toolCondition';
import { MAX_IMAGE_UPLOAD_BYTES, MAX_TOTAL_IMAGE_UPLOAD_BYTES, prepareImageFiles } from '../lib/imageUpload';

type ToolFormData = {
  name: string;
  description: string;
  disabled: boolean;
  pricePerDay: string;
  address: string;
  city: string;
  postalCode: string;
  categories: string[];
  primaryImageUrl: string;
  imageFiles: File[];
  brandName: string;
  model: string;
  powerDescription: string;
  condition: ToolCondition | '';
  weightGrams: string;
  ageMonths: string;
  maxAvailableCount: string;
  whatsIncluded: string[];
  usageInstructions: string;
  availabilityPeriods: AvailabilityPeriod[];
};

type ExistingToolImage = {
  id: string;
  url: string;
  position: number;
  primary: boolean;
};

type ImageOrderItem = {
  kind: 'existing' | 'new';
  id: string;
};

const EditTool: React.FC = () => {
  const canMutateExistingImages = import.meta.env.DEV || import.meta.env.VITE_ENABLE_EXISTING_IMAGE_MUTATIONS === 'true';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [removedImageIds, setRemovedImageIds] = useState<string[]>([]);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [existingImages, setExistingImages] = useState<ExistingToolImage[]>([]);
  const [newImageItemIds, setNewImageItemIds] = useState<string[]>([]);
  const [imageOrder, setImageOrder] = useState<ImageOrderItem[]>([]);
  const [primaryImageRef, setPrimaryImageRef] = useState<ImageOrderItem | null>(null);
  const [originalExistingImageIds, setOriginalExistingImageIds] = useState<string[]>([]);
  const [originalPrimaryImageRef, setOriginalPrimaryImageRef] = useState<ImageOrderItem | null>(null);
  const [originalAvailabilityPeriods, setOriginalAvailabilityPeriods] = useState<AvailabilityPeriod[]>([]);
  
  const { data: toolData, loading: loadingTool } = useQuery(GET_TOOL_BY_ID, {
    variables: { id, availabilityStartDate: new Date().toISOString().split('T')[0] },
    fetchPolicy: 'network-only',
    skip: !user,
  });
  const tool = toolData?.tool;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signin', { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!authLoading && user && tool?.isOwnTool === false) {
      toast.error('Du kan kun redigere dit eget værktøj');
      navigate('/', { replace: true });
    }
  }, [authLoading, user, tool, navigate]);

  const [formData, setFormData] = useState<ToolFormData>({
    name: '',
    description: '',
    disabled: false,
    pricePerDay: '',
    address: '',
    city: '',
    postalCode: '',
    categories: [] as string[],
    primaryImageUrl: '',
    imageFiles: [],
    brandName: '',
    model: '',
    powerDescription: '',
    condition: '',
    weightGrams: '',
    ageMonths: '',
    maxAvailableCount: '1',
    whatsIncluded: [''],
    usageInstructions: '',
    availabilityPeriods: [],
  });

  const addImages = async (files: FileList) => {
    const { files: preparedFiles, errors } = await prepareImageFiles(files);

    if (errors.length > 0) {
      setError(errors[0]);
    }

    if (preparedFiles.length === 0) {
      return;
    }

    const currentTotalBytes = formData.imageFiles.reduce((sum, file) => sum + file.size, 0);
    const acceptedFiles: File[] = [];
    let nextTotalBytes = currentTotalBytes;

    for (const file of preparedFiles) {
      if (nextTotalBytes + file.size <= MAX_TOTAL_IMAGE_UPLOAD_BYTES) {
        acceptedFiles.push(file);
        nextTotalBytes += file.size;
      }
    }

    if (acceptedFiles.length === 0) {
      setError(`Samlet upload er for stor. Maks ${Math.round(MAX_TOTAL_IMAGE_UPLOAD_BYTES / (1024 * 1024))}MB for alle billeder.`);
      return;
    }

    if (acceptedFiles.length < preparedFiles.length) {
      setError(`Nogle billeder blev ikke tilføjet pga. samlet max på ${Math.round(MAX_TOTAL_IMAGE_UPLOAD_BYTES / (1024 * 1024))}MB.`);
    }

    const generateNewImageItemId = () => {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
      return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    };

    const acceptedFileItemIds = acceptedFiles.map(() => generateNewImageItemId());

    setNewImageItemIds(prev => [...prev, ...acceptedFileItemIds]);
    setImageOrder(prev => [
      ...prev,
      ...acceptedFileItemIds.map((imageId) => ({ kind: 'new' as const, id: imageId })),
    ]);
    setFormData(prev => ({
      ...prev,
      imageFiles: [...prev.imageFiles, ...acceptedFiles]
    }));
  };

  const removeNewImage = (imageId: string) => {
    const imageIndex = newImageItemIds.indexOf(imageId);
    if (imageIndex === -1) return;

    setNewImageItemIds(prev => prev.filter((id) => id !== imageId));
    setImageOrder(prev => prev.filter((item) => !(item.kind === 'new' && item.id === imageId)));
    setFormData(prev => {
      return {
        ...prev,
        imageFiles: prev.imageFiles.filter((_, i) => i !== imageIndex),
      };
    });
  };

  const moveImageInOrder = (index: number, direction: 'up' | 'down') => {
    setImageOrder(prev => {
      const newOrder = [...prev];
      const newIndex = direction === 'up' ? index - 1 : index + 1;

      if (newIndex < 0 || newIndex >= newOrder.length) return prev;

      [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
      return newOrder;
    });
  };

  const removeExistingImage = (imageId: string) => {
    setRemovedImageIds(prev => [...prev, imageId]);
    setImageOrder(prev => prev.filter((item) => !(item.kind === 'existing' && item.id === imageId)));
  };

  const setPrimaryImage = (imageRef: ImageOrderItem) => {
    setPrimaryImageRef(imageRef);
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      void addImages(files);
    }
  };

  const [categories, setCategories] = useState<any[]>([]);
  const { data: catData } = useQuery(GET_CATEGORIES);
  const [updateTool] = useMutation(UPDATE_TOOL);

  useEffect(() => {
    if (catData) {
      setCategories(catData.categories);
    }
  }, [catData]);

  useEffect(() => {
    if (tool) {
      const sortedExistingImages: ExistingToolImage[] = (tool.images || [])
        .map((image: ExistingToolImage) => ({
          id: image.id,
          url: image.url,
          position: image.position,
          primary: image.primary,
        }))
        .sort((a: ExistingToolImage, b: ExistingToolImage) => a.position - b.position);

      setExistingImages(sortedExistingImages);
      setOriginalExistingImageIds(sortedExistingImages.map(image => image.id));
      setRemovedImageIds([]);
      setImageOrder(sortedExistingImages.map((image) => ({ kind: 'existing' as const, id: image.id })));

      const primaryImg = sortedExistingImages.find((image) => image.primary);
      const initialPrimaryRef = primaryImg?.id
        ? { kind: 'existing' as const, id: primaryImg.id }
        : (sortedExistingImages[0]?.id ? { kind: 'existing' as const, id: sortedExistingImages[0].id } : null);
      setPrimaryImageRef(initialPrimaryRef);
      setOriginalPrimaryImageRef(initialPrimaryRef);

      setFormData({
        name: tool.name || '',
        description: tool.description || '',
        disabled: !!tool.disabled,
        pricePerDay: tool.pricePerDayCents ? (tool.pricePerDayCents / 100).toString() : '',
        address: tool.address || '',
        city: tool.city || '',
        postalCode: tool.postalCode || '',
        categories: tool.categories?.map((c: { id: any; }) => c.id) || [],
        primaryImageUrl: tool.primaryImageUrl || '',
        imageFiles: [],
        brandName: tool.brandName || '',
        model: tool.model || '',
        powerDescription: tool.powerDescription || '',
        condition: normalizeToolCondition(tool.condition),
        weightGrams: tool.weightGrams ? tool.weightGrams.toString() : '',
        ageMonths: tool.ageMonths ? tool.ageMonths.toString() : '',
        maxAvailableCount: tool.maxAvailableCount ? tool.maxAvailableCount.toString() : '1',
        whatsIncluded: tool.includedItems || [''],
        usageInstructions: tool.usageInstructions || '',
        availabilityPeriods: tool.toolAvailabilityPeriods?.map((period: any) => ({
          id: period.id,
          startDate: period.startDate,
          endDate: period.endDate,
          available: period.available,
        })) || [
          {
            startDate: new Date().toISOString().split('T')[0],
            endDate: undefined,
            available: true,
          },
        ],
      });

      setNewImageItemIds([]);
      
      // Save original availability periods for comparison
      setOriginalAvailabilityPeriods(
        tool.toolAvailabilityPeriods?.map((period: any) => ({
          id: period.id,
          startDate: period.startDate,
          endDate: period.endDate,
          available: period.available,
        })) || [
          {
            startDate: new Date().toISOString().split('T')[0],
            endDate: undefined,
            available: true,
          },
        ]
      );
    }
  }, [tool]);

  useEffect(() => {
    if (imageOrder.length === 0) {
      setPrimaryImageRef(null);
      return;
    }

    if (!primaryImageRef) {
      setPrimaryImageRef(imageOrder[0]);
      return;
    }

    const hasCurrentPrimary = imageOrder.some(
      (item) => item.kind === primaryImageRef.kind && item.id === primaryImageRef.id
    );

    if (!hasCurrentPrimary) {
      setPrimaryImageRef(imageOrder[0]);
    }
  }, [imageOrder, primaryImageRef]);

  if (authLoading || !user || loadingTool) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-gray-600">Indlæser værktøj...</p>
          </div>
        </div>
      </div>
    );
  }

  if (tool?.isOwnTool === false) {
    return null;
  }

  if (!tool) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900">Værktøj ikke fundet</h2>
            <p className="mt-2 text-gray-600">Værktøjet du prøver at redigere findes ikke.</p>
            <Button variant="primary" onClick={() => navigate(-1)} className="mt-4">
              Tilbage til dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const encodeFileToBase64 = (file: File) =>
    new Promise<{ base64: string; filename: string; contentType: string }>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve({
          base64,
          filename: file.name,
          contentType: file.type,
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleCategoryToggle = (category: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleIncludedItemChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      whatsIncluded: prev.whatsIncluded.map((item, i) => 
        i === index ? value : item
      )
    }));
  };

  const addIncludedItem = () => {
    setFormData(prev => ({
      ...prev,
      whatsIncluded: [...prev.whatsIncluded, '']
    }));
  };

  const removeIncludedItem = (index: number) => {
    if (formData.whatsIncluded.length > 1) {
      setFormData(prev => ({
        ...prev,
        whatsIncluded: prev.whatsIncluded.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!formData.name || !formData.description || !formData.pricePerDay) {
        throw new Error('Udfyld venligst alle påkrævede felter');
      }

      if (formData.categories.length === 0) {
        throw new Error('Vælg venligst mindst én kategori');
      }

      const price = parseFloat(formData.pricePerDay);
      if (isNaN(price) || price <= 0) {
        throw new Error('Indtast venligst en gyldig pris');
      }

      // Encode new images to base64
      const encodedImages = await Promise.all(
        formData.imageFiles.map((file) => encodeFileToBase64(file))
      );

      const encodedNewImageById = new Map<string, { base64: string; filename: string; contentType: string }>();
      newImageItemIds.forEach((imageId, index) => {
        const encodedImage = encodedImages[index];
        if (encodedImage) {
          encodedNewImageById.set(imageId, encodedImage);
        }
      });

      const activeExistingImages = existingImages.filter((image) => !removedImageIds.includes(image.id));
      const activeExistingImageIdSet = new Set(activeExistingImages.map((image) => image.id));
      const currentExistingImageIds = imageOrder
        .filter((item) => item.kind === 'existing' && activeExistingImageIdSet.has(item.id))
        .map((item) => item.id);
      const hasExistingImageOrderChanged =
        currentExistingImageIds.length !== originalExistingImageIds.length ||
        currentExistingImageIds.some((imageId, index) => imageId !== originalExistingImageIds[index]);

      const defaultImageOrder: ImageOrderItem[] = [
        ...activeExistingImages.map((image) => ({ kind: 'existing' as const, id: image.id })),
        ...newImageItemIds.map((imageId) => ({ kind: 'new' as const, id: imageId })),
      ];

      const hasImageOrderChanged =
        imageOrder.length !== defaultImageOrder.length ||
        imageOrder.some((item, index) => {
          const defaultItem = defaultImageOrder[index];
          return !defaultItem || defaultItem.kind !== item.kind || defaultItem.id !== item.id;
        });

      const isSameImageRef = (a: ImageOrderItem | null, b: ImageOrderItem | null) => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return a.kind === b.kind && a.id === b.id;
      };

      const hasPrimaryImageChanged = !isSameImageRef(primaryImageRef, originalPrimaryImageRef);

      const hasExistingImageChanges =
        removedImageIds.length > 0 ||
        hasExistingImageOrderChanged ||
        hasImageOrderChanged ||
        hasPrimaryImageChanged;

      if (hasExistingImageChanges && !canMutateExistingImages) {
        throw new Error('Midlertidigt i produktion: du kan tilføje nye billeder, men ikke ændre rækkefølge/fjerne/ændre primær blandt eksisterende endnu.');
      }

      // Check if availability periods have changed
      const hasAvailabilityChanged = () => {
        if (originalAvailabilityPeriods.length !== formData.availabilityPeriods.length) {
          return true;
        }
        
        // Compare each period
        for (let i = 0; i < formData.availabilityPeriods.length; i++) {
          const current = formData.availabilityPeriods[i];
          const original = originalAvailabilityPeriods.find(p => p.id === current.id);
          
          // New period (no id) or period not found in original
          if (!current.id || !original) {
            return true;
          }
          
          // Check if any field has changed
          if (
            original.startDate !== current.startDate ||
            original.endDate !== current.endDate ||
            original.available !== current.available
          ) {
            return true;
          }
        }
        
        // Check if any original period was deleted
        for (const original of originalAvailabilityPeriods) {
          if (!formData.availabilityPeriods.find(p => p.id === original.id)) {
            return true;
          }
        }
        
        return false;
      };

      // Prepare update input matching backend schema
      const updateInput: any = {
        id,
        name: formData.name,
        description: formData.description,
        disabled: formData.disabled,
        pricePerDayCents: Math.round(price * 100),
        address: formData.address,
        city: formData.city,
        postalCode: formData.postalCode,
        categoryIds: formData.categories,
        brandName: formData.brandName || null,
        model: formData.model || null,
        powerDescription: formData.powerDescription || null,
        condition: formData.condition || null,
        weightGrams: formData.weightGrams ? parseInt(formData.weightGrams) : null,
        ageMonths: formData.ageMonths ? parseInt(formData.ageMonths) : null,
        maxAvailableCount: formData.maxAvailableCount ? parseInt(formData.maxAvailableCount) : 1,
        includedItems: formData.whatsIncluded.filter(item => item.trim() !== ''),
        usageInstructions: formData.usageInstructions || null,
      };

      // Only include availability periods if they have changed
      if (hasAvailabilityChanged()) {
        updateInput.toolAvailabilityPeriods = formData.availabilityPeriods.map(period => ({
          id: period.id || undefined,
          startDate: period.startDate,
          endDate: period.endDate || null,
          available: period.available,
        }));
        updateInput.replaceToolAvailabilityPeriods = true;
      }

      if (hasExistingImageChanges) {
        const effectivePrimaryRef =
          primaryImageRef && imageOrder.some((item) => item.kind === primaryImageRef.kind && item.id === primaryImageRef.id)
            ? primaryImageRef
            : (imageOrder[0] || null);

        const reorderedImagesPayload: any[] = [];

        imageOrder.forEach((item, index) => {
          if (item.kind === 'existing') {
            if (!activeExistingImageIdSet.has(item.id)) {
              return;
            }

            reorderedImagesPayload.push({
              id: item.id,
              position: index + 1,
              primary: !!effectivePrimaryRef && effectivePrimaryRef.kind === 'existing' && effectivePrimaryRef.id === item.id,
            });
            return;
          }

          const encodedImage = encodedNewImageById.get(item.id);
          if (!encodedImage) {
            return;
          }

          reorderedImagesPayload.push({
            image: encodedImage,
            position: index + 1,
            primary: !!effectivePrimaryRef && effectivePrimaryRef.kind === 'new' && effectivePrimaryRef.id === item.id,
          });
        });

        updateInput.images = reorderedImagesPayload;
        updateInput.replaceImages = true;
      }

      // Add new images if any
      if (!hasExistingImageChanges && encodedNewImageById.size > 0) {
        const effectivePrimaryRef =
          primaryImageRef && imageOrder.some((item) => item.kind === primaryImageRef.kind && item.id === primaryImageRef.id)
            ? primaryImageRef
            : (imageOrder[0] || null);

        const existingImageCount = activeExistingImages.length;
        updateInput.images = newImageItemIds.flatMap((imageId, index) => {
          const encodedImage = encodedNewImageById.get(imageId);
          if (!encodedImage) {
            return [];
          }

          return [{
            image: encodedImage,
            position: existingImageCount + index + 1,
            primary: existingImageCount === 0 && !!effectivePrimaryRef && effectivePrimaryRef.kind === 'new' && effectivePrimaryRef.id === imageId,
          }];
        });
        updateInput.replaceImages = false; // Add to existing images
      }

      const { data } = await updateTool({
        variables: {
          input: updateInput,
        },
      });

      if (data?.updateTool?.errors && data.updateTool.errors.length > 0) {
        throw new Error(data.updateTool.errors.join(', '));
      }
      
      navigate(`/tool/${id}?updated=true`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Kunne ikke opdatere værktøj';
      if (errorMessage.includes('Field is not defined on ToolImageInput') && errorMessage.includes('.id')) {
        setError('Backend understøtter ikke opdatering af eksisterende billedreferencer endnu. Kontakt backend eller brug kun tilføjelse af nye billeder.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const activeExistingImages = existingImages.filter((img) => !removedImageIds.includes(img.id));
  const existingImageById = new Map(activeExistingImages.map((image) => [image.id, image]));
  const newImageById = new Map(
    newImageItemIds
      .map((imageId, index) => {
        const file = formData.imageFiles[index];
        return file ? ([imageId, file] as const) : null;
      })
      .filter((entry): entry is readonly [string, File] => entry !== null)
  );
  const orderedImages = imageOrder.filter((item) => (
    item.kind === 'existing' ? existingImageById.has(item.id) : newImageById.has(item.id)
  ));

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate(-1)} className="flex items-center text-green-600 mb-8 hover:text-green-700">
            <ArrowLeft className="h-5 w-5 mr-2" />
            <span>Tilbage</span>
          </button>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Rediger dit værktøj</h1>
              <p className="mt-2 text-gray-600">
                Opdater oplysninger om dit værktøj
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Værktøjsstatus</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Deaktiveret værktøj kan ikke fremsøges eller bookes.
                    </p>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.disabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, disabled: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    Deaktiver værktøj
                  </label>
                </div>

                {formData.disabled && (
                  <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3">
                    <div className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-amber-700 mr-2 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800">
                        Dette værktøj er deaktiveret. Det er skjult fra søgning og kan ikke bookes, før du aktiverer det igen.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Tool Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Værktøjs navn <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="F.eks., DeWalt skruemaskine"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Beskrivelse <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="Beskriv dit værktøj, dets stand og eventuelle særlige funktioner..."
                  required
                />
              </div>

              {/* Price */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                    Pris per dag <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm" style={{display: 'flex', alignItems: 'center'}}>
                    <input
                      type="number"
                      id="price"
                      min="0"
                      step="0.01"
                      value={formData.pricePerDay}
                      onChange={(e) => setFormData(prev => ({ ...prev, pricePerDay: e.target.value }))}
                      className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                      placeholder="0,00"
                      required
                      style={{ textAlign: "right" }}
                    />
                    &nbsp;
                    <div>kr.</div>
                  </div>
                </div>
              </div>

              {/* Categories */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Kategorier <span className="text-red-500">*</span></h2>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleCategoryToggle(category.id)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                        formData.categories.includes(category.id)
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Adresse */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Adresse</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Værktøjets placering er skjult for lejer indtil bookingen er accepteret
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                      Vejnavn og husnummer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="address"
                      placeholder="F.eks. Vestergade 12"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                        Postnummer <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="postalCode"
                        placeholder="F.eks. 8000"
                        value={formData.postalCode}
                        onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                        By <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="city"
                        placeholder="F.eks. Aarhus C"
                        value={formData.city}
                        onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Specifikationer */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Specifikationer</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Tilføj tekniske detaljer om dit værktøj
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="brandName" className="block text-sm font-medium text-gray-700 mb-1">
                      Mærke
                    </label>
                    <input
                      type="text"
                      id="brandName"
                      placeholder="F.eks. DeWalt, Bosch"
                      value={formData.brandName}
                      onChange={(e) => setFormData(prev => ({ ...prev, brandName: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-1">
                      Model
                    </label>
                    <input
                      type="text"
                      id="model"
                      placeholder="F.eks. DCD777C2"
                      value={formData.model}
                      onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="powerDescription" className="block text-sm font-medium text-gray-700 mb-1">
                      Effekt
                    </label>
                    <input
                      type="text"
                      id="powerDescription"
                      placeholder="F.eks. 20V, 1500W"
                      value={formData.powerDescription}
                      onChange={(e) => setFormData(prev => ({ ...prev, powerDescription: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="condition" className="block text-sm font-medium text-gray-700 mb-1">
                      Stand
                    </label>
                    <select
                      id="condition"
                      value={formData.condition}
                      onChange={(e) => setFormData(prev => ({ ...prev, condition: e.target.value as ToolCondition | '' }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    >
                      <option value="">Vælg stand</option>
                      {TOOL_CONDITION_OPTIONS.map((conditionOption) => (
                        <option key={conditionOption.value} value={conditionOption.value}>
                          {conditionOption.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="weightGrams" className="block text-sm font-medium text-gray-700 mb-1">
                      Vægt (gram)
                    </label>
                    <input
                      type="number"
                      id="weightGrams"
                      placeholder="F.eks. 1500"
                      value={formData.weightGrams}
                      onChange={(e) => setFormData(prev => ({ ...prev, weightGrams: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="ageMonths" className="block text-sm font-medium text-gray-700 mb-1">
                      Alder (måneder)
                    </label>
                    <input
                      type="number"
                      id="ageMonths"
                      placeholder="F.eks. 24"
                      value={formData.ageMonths}
                      onChange={(e) => setFormData(prev => ({ ...prev, ageMonths: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="maxAvailableCount" className="block text-sm font-medium text-gray-700 mb-1">
                      Maks. antal tilgængelige
                    </label>
                    <input
                      type="number"
                      id="maxAvailableCount"
                      min="1"
                      placeholder="F.eks. 1"
                      value={formData.maxAvailableCount}
                      onChange={(e) => setFormData(prev => ({ ...prev, maxAvailableCount: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Hvad er inkluderet */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Hvad er inkluderet</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Angiv alle tilbehør der følger med værktøjet (kuffert, batteri, bor osv.)
                </p>
                
                <div className="space-y-3">
                  {formData.whatsIncluded.map((item, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="F.eks. Kuffert, Ekstra batteri, Borsæt"
                          value={item}
                          onChange={(e) => handleIncludedItemChange(index, e.target.value)}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeIncludedItem(index)}
                        className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500"
                        disabled={formData.whatsIncluded.length === 1}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
                
                <button
                  type="button"
                  onClick={addIncludedItem}
                  className="mt-3 inline-flex items-center text-sm text-green-600 hover:text-green-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Tilføj element
                </button>
              </div>

              {/* Brugsanvisning */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Brugsanvisning</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Giv hjælpsomme instruktioner til lånere om hvordan de sikkert bruger dit værktøj
                </p>
                
                <textarea
                  rows={6}
                  value={formData.usageInstructions}
                  onChange={(e) => setFormData(prev => ({ ...prev, usageInstructions: e.target.value }))}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="Beskriv trin-for-trin instruktioner, sikkerhedstips og særlige hensyn ved brug af dit værktøj..."
                />
              </div>

              {/* Tilgængelighed */}
              <div>
                <ToolAvailabilityCalendar
                  periods={formData.availabilityPeriods}
                  onChange={(periods) => setFormData(prev => ({ ...prev, availabilityPeriods: periods }))}
                  toolId={id}
                />
              </div>

              {/* Image Upload */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Billeder</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Tilføj eller opdater billeder af dit værktøj
                </p>

                {orderedImages.length > 0 && (
                  <div className="mb-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {orderedImages.map((item, index) => {
                        const existingImage = item.kind === 'existing' ? existingImageById.get(item.id) : null;
                        const newFile = item.kind === 'new' ? newImageById.get(item.id) : null;

                        if (item.kind === 'existing' && !existingImage) {
                          return null;
                        }

                        if (item.kind === 'new' && !newFile) {
                          return null;
                        }

                        const previewSrc = item.kind === 'existing'
                          ? existingImage!.url
                          : URL.createObjectURL(newFile!);
                        const isPrimary = !!primaryImageRef && primaryImageRef.kind === item.kind && primaryImageRef.id === item.id;

                        return (
                          <div key={`${item.kind}-${item.id}`} className="relative border-2 rounded-lg p-2 bg-white">
                            <div className="absolute top-1 left-1 z-10 flex flex-col gap-1">
                              {isPrimary && (
                                <div className="bg-green-600 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
                                  <Star className="h-3 w-3 fill-current" />
                                  Primær
                                </div>
                              )}
                              {item.kind === 'new' && (
                                <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded-md">
                                  Ny
                                </div>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() => item.kind === 'existing' ? removeExistingImage(item.id) : removeNewImage(item.id)}
                              className="absolute top-1 right-1 p-1 bg-white rounded-full shadow-md text-gray-400 hover:text-red-600 hover:bg-red-50 z-10"
                              title="Fjern billede"
                            >
                              <X className="h-4 w-4" />
                            </button>

                            <img
                              src={previewSrc}
                              alt={`Billede ${index + 1}`}
                              className="w-full h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => setPreviewImageUrl(previewSrc)}
                            />

                            <div className="mt-2 flex items-center justify-between gap-2">
                              <div className="flex gap-1">
                                <button
                                  type="button"
                                  onClick={() => moveImageInOrder(index, 'up')}
                                  disabled={index === 0}
                                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Flyt op"
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveImageInOrder(index, 'down')}
                                  disabled={index === orderedImages.length - 1}
                                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Flyt ned"
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </button>
                              </div>

                              {!isPrimary && (
                                <button
                                  type="button"
                                  onClick={() => setPrimaryImage(item)}
                                  className="p-1 text-gray-400 hover:text-green-600"
                                  title="Sæt som primær"
                                >
                                  <Star className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Upload area */}
                <div 
                  className={`flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                    isDragging 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300'
                  }`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="space-y-1 text-center">
                    <Upload className={`mx-auto h-12 w-12 ${
                      isDragging ? 'text-green-500' : 'text-gray-400'
                    }`} />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="image-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500"
                      >
                        <span>Tilføj billeder</span>
                        <input
                          id="image-upload"
                          name="image-upload"
                          type="file"
                          multiple
                          className="sr-only"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files && e.target.files.length > 0) {
                              void addImages(e.target.files);
                            }
                          }}
                        />
                      </label>
                      <p className="pl-1">eller træk og slip</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, WEBP, GIF. Komprimeres automatisk. Maks {Math.round(MAX_IMAGE_UPLOAD_BYTES / (1024 * 1024))}MB pr. billede.
                    </p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/tool/${id}`)}
                >
                  Annullér
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={isLoading}
                  icon={<Save className="h-4 w-4 mr-2" />}
                >
                  Gem ændringer
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImageUrl && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImageUrl(null)}
        >
          <button
            onClick={() => setPreviewImageUrl(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2 bg-black bg-opacity-50 rounded-full"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={previewImageUrl}
            alt="Preview"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default EditTool;
